import { getTranslation } from 'helpers/getTranslation';

import {
  resolveAuthSourceFromProperties,
  translateAuthSource,
  translateCheckoutFailureStage,
  translateOnboardingOption,
  translateOtpErrorType,
  translatePaymentErrorCode,
  translatePaywallTrigger,
  translatePlacement,
  translatePlanPeriod,
  translateSubscriptionCancellationType,
  translateTrainerMode,
} from './analyticsPropertyLabels';
import { eventNameToTranslationKey } from './analyticsEventUtils';
import {
  describeClientError,
  getHttpStatusKind,
  getHttpStatusLabel,
} from './journeyEndpointLabels';

export { formatRageClickTarget } from './journeyEndpointLabels';

const CONTEXT_PREFIX = 'student_page.timeline.context';

const readNonEmptyString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const readFiniteNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const readPositiveNumber = (value: unknown): number | null => {
  const number = readFiniteNumber(value);

  return number != null && number > 0 ? number : null;
};

const joinParts = (parts: Array<string | null | undefined>): string | null => {
  const filtered = parts.filter((part): part is string =>
    Boolean(part && part.length > 0),
  );

  return filtered.length > 0 ? filtered.join(' · ') : null;
};

const capitalizeFirst = (value: string | null): string | null =>
  value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value;

const translateContext = (key: string, params?: Record<string, unknown>) =>
  getTranslation(`${CONTEXT_PREFIX}.${key}`, params);

/** `null` when the key has no translation (i18next returns the key itself). */
const translateOptional = (key: string, params?: Record<string, unknown>) => {
  const translated = getTranslation(key, params);

  return translated === key ? null : translated;
};

/* Formatting */

const readEmailDomain = (email: string): string | null => {
  const atIndex = email.lastIndexOf('@');

  if (atIndex <= 0 || atIndex === email.length - 1) {
    return null;
  }

  return email
    .slice(atIndex + 1)
    .trim()
    .toLowerCase();
};

const resolveOtpEmailLabel = (
  emailDomain: string | null,
  studentEmail?: string | null,
): string | null => {
  const trimmedStudentEmail = studentEmail?.trim();

  if (trimmedStudentEmail && trimmedStudentEmail.includes('@')) {
    const studentDomain = readEmailDomain(trimmedStudentEmail);

    if (!emailDomain || studentDomain === emailDomain.toLowerCase()) {
      return translateContext('otp_email', { email: trimmedStudentEmail });
    }
  }

  if (!emailDomain) {
    return null;
  }

  return translateContext('otp_email_domain', { domain: emailDomain });
};

/** «1,8 с» with one decimal — for response times and click bursts. */
const formatSecondsLabel = (durationMs: number | null): string | null =>
  durationMs != null
    ? translateContext('duration_seconds', {
        value: Math.round(Math.max(durationMs, 0) / 100) / 10,
      })
    : null;

/** «40 с», «4 мин», «1 ч 5 мин» — for time spent in a lesson. */
const formatWholeDuration = (seconds: number | null): string | null => {
  if (seconds == null || seconds < 0) {
    return null;
  }

  const totalSeconds = Math.round(seconds);

  if (totalSeconds < 60) {
    return translateContext('duration_seconds', { value: totalSeconds });
  }

  const totalMinutes = Math.round(totalSeconds / 60);

  if (totalMinutes < 60) {
    return translateContext('duration_minutes', { minutes: totalMinutes });
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return minutes > 0
    ? translateContext('duration_hours_minutes', { hours, minutes })
    : translateContext('duration_hours', { hours });
};

/** «5,4 с — обычно до 3 с». */
const formatAgainstThreshold = (
  durationMs: number | null,
  thresholdMs: number | null,
): string | null => {
  const duration = formatSecondsLabel(durationMs);

  if (!duration) {
    return null;
  }

  const threshold = formatSecondsLabel(thresholdMs);

  return threshold
    ? translateContext('duration_vs_threshold', { duration, threshold })
    : duration;
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  RUB: '₽',
  RUR: '₽',
  USD: '$',
  EUR: '€',
};

const formatPrice = (
  amount: number | null,
  currency: string | null,
): string | null => {
  if (amount == null || amount <= 0) {
    return null;
  }

  const code = currency?.toUpperCase() ?? 'RUB';

  return translateContext('price', {
    value: amount,
    currency: CURRENCY_SYMBOLS[code] ?? code,
  });
};

const formatPriceFromProperties = (properties: Record<string, unknown>) =>
  formatPrice(
    readFiniteNumber(properties.revenue) ??
      readFiniteNumber(properties.plan_price) ??
      readFiniteNumber(properties.price),
    readNonEmptyString(properties.currency) ??
      readNonEmptyString(properties.plan_currency),
  );

/** Machine-looking values (price ids, lookup keys) are never shown as plan names. */
const MACHINE_VALUE_PATTERN = /^(?=.*[_\d])[a-z0-9_.:-]+$/i;

/** «тариф «Месяц»» from plan_id / plan_key / plan, or a human `plan_name`. */
const formatPlanLabel = (
  properties: Record<string, unknown>,
): string | null => {
  const planKey =
    readNonEmptyString(properties.plan_id) ??
    readNonEmptyString(properties.plan_key) ??
    readNonEmptyString(properties.plan);
  const planName = readNonEmptyString(properties.plan_name);
  const name =
    (planKey ? translatePlanPeriod(planKey) : null) ??
    (planName && !MACHINE_VALUE_PATTERN.test(planName) ? planName : null);

  return name ? translateContext('plan', { name }) : null;
};

const formatPaywallTrigger = (properties: Record<string, unknown>) => {
  const trigger =
    readNonEmptyString(properties.paywall_trigger) ??
    readNonEmptyString(properties.trigger);

  return trigger ? translatePaywallTrigger(trigger) : null;
};

const formatPlacement = (properties: Record<string, unknown>) => {
  const source = readNonEmptyString(properties.source);

  return source && source !== 'server' ? translatePlacement(source) : null;
};

const formatPercent = (key: string, value: number | null) =>
  value != null
    ? translateContext(key, {
        value: Math.round(Math.min(Math.max(value, 0), 100)),
      })
    : null;

const formatSolvedTasks = (count: number | null): string | null => {
  if (count == null || count < 0) {
    return null;
  }

  return count === 0
    ? translateContext('tasks_solved_none')
    : translateContext('tasks_solved', { count });
};

/** «Урок 3 из 12 (название не найдено)» / «Урок не найден в каталоге (возможно, удалён)». */
export const formatMissingLessonLabel = (
  properties: Record<string, unknown>,
): string => {
  const lessonIndex = readPositiveNumber(properties.lesson_index);
  const lessonsTotal = readPositiveNumber(properties.lessons_total);

  return lessonIndex != null && lessonsTotal != null
    ? translateContext('lesson_position_unnamed', {
        number: lessonIndex,
        total: lessonsTotal,
      })
    : translateContext('lesson_not_found');
};

/* Public translators */

/** Human label for a Slow Screen `stage`; `null` when the stage is unknown. */
export const translateSlowScreenStage = (stage: string): string | null =>
  translateOptional(`${CONTEXT_PREFIX}.slow_screen_stages.${stage}`);

/**
 * Title of a Slow Screen row: «Урок долго открывался в Figma»; one that never
 * finished loading (`completed: false`) says so: «Урок так и не открылся в Figma».
 */
export const getSlowScreenTitle = (properties?: Record<string, unknown>) => {
  const stage = readNonEmptyString(properties?.stage);
  const variant = properties?.completed === false ? '_not_completed' : '';

  return (
    (stage
      ? translateOptional(
          `student_page.timeline.slow_screen_titles${variant}.${stage}`,
        )
      : null) ??
    getTranslation(`student_page.timeline.events.slow_screen${variant}`)
  );
};

/** Exit-reason sentence from `student_page.journey.exit_reasons`; `null` when unknown. */
export const translateSessionEndReason = (reason: string): string | null =>
  translateOptional(`student_page.journey.exit_reasons.${reason}`);

/** Title of the exit row by reason and platform: «Закрыл плагин», «Закрыл вкладку»… */
export const getSessionEndTitle = (
  properties?: Record<string, unknown>,
  platform?: string | null,
) => {
  const reason = readNonEmptyString(properties?.reason);
  const prefix = 'student_page.timeline.session_end_titles';

  if (reason === 'closed') {
    const variant =
      platform === 'Plugin'
        ? 'closed_plugin'
        : platform === 'Web'
        ? 'closed_web'
        : 'closed';

    return getTranslation(`${prefix}.${variant}`);
  }

  return (
    (reason ? translateOptional(`${prefix}.${reason}`) : null) ??
    getTranslation('student_page.timeline.events.session_ended')
  );
};

/* Subtitle builders */

type ContextBuilder = (
  properties: Record<string, unknown>,
  options?: FormatEventContextOptions,
) => Array<string | null | undefined>;

const buildPlanContext: ContextBuilder = (properties) => [
  formatPlanLabel(properties),
  formatPriceFromProperties(properties),
  properties.has_discount === true ? translateContext('with_discount') : null,
];

const buildCheckoutEntryContext: ContextBuilder = (properties) => [
  ...buildPlanContext(properties),
  formatPaywallTrigger(properties),
];

const buildPaywallContext: ContextBuilder = (properties) => [
  formatPlacement(properties),
  formatPaywallTrigger(properties),
  formatPlanLabel(properties),
];

/** «Задача 2 из 5 · совпадение 87 %»: «верно / неверно» is the row title («Ответил верно»). */
const buildTaskAnsweredContext: ContextBuilder = (properties) => {
  const matchPercent = readFiniteNumber(properties.match_percent);
  const taskNumber =
    readPositiveNumber(properties.task_index) ??
    readPositiveNumber(properties.task_number);
  const taskTotal =
    readPositiveNumber(properties.tasks_total) ??
    readPositiveNumber(properties.task_total);

  return [
    taskNumber != null
      ? taskTotal != null
        ? translateContext('task_position', {
            number: taskNumber,
            total: taskTotal,
          })
        : translateContext('task_number', { number: taskNumber })
      : null,
    formatPercent('match_percent', matchPercent),
  ];
};

const buildOnboardingStepContext: ContextBuilder = (properties) => {
  const stepIndex = readFiniteNumber(properties.step_index);
  const stepsTotal = readPositiveNumber(properties.steps_total);
  const optionKey = readNonEmptyString(properties.option_key);
  const answer = optionKey ? translateOnboardingOption(optionKey) : null;

  if (stepIndex == null || stepIndex < 0 || stepsTotal == null) {
    return [answer ? translateContext('onboarding_answer', { answer }) : null];
  }

  /* Web onboarding sends a 0-based step_index. */
  const params = { number: stepIndex + 1, total: stepsTotal };

  return [
    answer
      ? translateContext('onboarding_step_answer', { ...params, answer })
      : translateContext('onboarding_step', params),
  ];
};

const STANDALONE_CONTEXT_BUILDERS: Record<string, ContextBuilder> = {
  client_error: (properties) => [describeClientError(properties)],
  api_error: (properties) => {
    const status = readFiniteNumber(properties.status);
    const errorCode = readNonEmptyString(properties.error_code);
    const kind = getHttpStatusKind(status, errorCode);
    const duration = formatSecondsLabel(
      readFiniteNumber(properties.duration_ms),
    );

    return [
      /* 4xx rejections are non-blocking warnings; the code itself is developer detail. */
      kind === 'rejected'
        ? translateContext('rejected_harmless')
        : getHttpStatusLabel(status, errorCode),
      duration
        ? translateContext(
            kind === 'network' || kind === 'timeout'
              ? 'waited'
              : 'response_after',
            { duration },
          )
        : null,
    ];
  },
  slow_request: (properties) => [
    formatAgainstThreshold(
      readFiniteNumber(properties.duration_ms),
      readFiniteNumber(properties.threshold_ms),
    ),
  ],
  /* «так и не открылся» is in the title of a screen that never finished loading. */
  slow_screen: (properties) => [
    formatAgainstThreshold(
      readFiniteNumber(properties.duration_ms),
      readFiniteNumber(properties.threshold_ms),
    ),
  ],
  rage_click: (properties) => {
    const duration = formatSecondsLabel(readFiniteNumber(properties.window_ms));

    return [
      duration
        ? getTranslation('student_page.timeline.rage_click.subtitle', {
            duration,
          })
        : null,
    ];
  },
  session_ended: (properties) => [
    properties.inferred === true
      ? translateContext('session_end_inferred')
      : null,
  ],

  otp_code_sent: (properties, options) => {
    const attemptNumber = readPositiveNumber(properties.attempt_number);

    return [
      resolveOtpEmailLabel(
        readNonEmptyString(properties.email_domain),
        options?.studentEmail,
      ),
      attemptNumber != null
        ? translateContext('otp_attempt', { number: attemptNumber })
        : null,
    ];
  },
  otp_code_verified: (properties) => {
    const attempts = readPositiveNumber(properties.attempts_count);

    return [
      attempts != null && attempts > 1
        ? translateContext('otp_attempts', { count: attempts })
        : null,
    ];
  },
  otp_failed: (properties) => {
    const errorType = readNonEmptyString(properties.error_type);

    return [errorType ? translateOtpErrorType(errorType) : null];
  },
  auth_viewed: (properties) => {
    const source = resolveAuthSourceFromProperties(properties);

    return [source ? translateAuthSource(source) : null];
  },
  login_completed: (properties) => [
    properties.is_new_user === true ? translateContext('new_account') : null,
  ],
  signup_survey_step_viewed: (properties) => {
    const stepNumber = readPositiveNumber(properties.step_number);

    return [
      stepNumber != null
        ? translateContext('survey_step', { number: stepNumber })
        : null,
    ];
  },
  activation_completed: (properties) => {
    const taskNumber = readPositiveNumber(properties.task_number);

    return [
      taskNumber != null
        ? translateContext('activation_task', { number: taskNumber })
        : null,
    ];
  },
  onboarding_step_completed: buildOnboardingStepContext,
  onboarding_skipped: (properties) => {
    const stepIndex = readFiniteNumber(properties.step_index);
    const stepsTotal = readPositiveNumber(properties.steps_total);

    return [
      stepIndex != null && stepIndex >= 0 && stepsTotal != null
        ? translateContext('onboarding_skipped_at', {
            number: stepIndex + 1,
            total: stepsTotal,
          })
        : null,
    ];
  },
  lesson_tour_skipped: (properties) => {
    const stepNumber = readPositiveNumber(properties.step_index);
    const stepsTotal = readPositiveNumber(properties.steps_total);

    return [
      stepNumber != null && stepsTotal != null
        ? translateContext('tour_skipped_at', {
            number: stepNumber,
            total: stepsTotal,
          })
        : null,
    ];
  },
  lesson_tour_completed: (properties) => {
    const stepsTotal = readPositiveNumber(properties.steps_total);

    return [
      stepsTotal != null
        ? translateContext('tour_steps', { count: stepsTotal })
        : null,
    ];
  },

  plan_selected: buildPlanContext,
  pricing_plan_clicked: buildPlanContext,
  subscription_checkout_clicked: buildPlanContext,
  subscription_cancel_confirmed: buildPlanContext,
  subscribe_page_viewed: buildPlanContext,
  subscribe_email_submitted: buildPlanContext,
  checkout_started: buildPlanContext,
  checkout_cancelled: buildPlanContext,
  checkout_abandoned: buildPlanContext,
  purchase_completed: buildPlanContext,
  payment_submitted: buildPlanContext,
  checkout_page_viewed: buildCheckoutEntryContext,
  checkout_redirected: buildCheckoutEntryContext,
  checkout_failed: (properties) => {
    const stage = readNonEmptyString(properties.failure_stage);

    return [
      stage ? translateCheckoutFailureStage(stage) : null,
      ...buildPlanContext(properties),
    ];
  },
  payment_succeeded: (properties) => [
    formatPlanLabel(properties),
    formatPriceFromProperties(properties),
    properties.is_first_payment === true
      ? translateContext('first_payment')
      : properties.is_renewal === true
      ? translateContext('renewal')
      : null,
  ],
  subscription_renewed: (properties) => {
    const renewalNumber = readPositiveNumber(properties.renewal_number);

    return [
      formatPlanLabel(properties),
      formatPriceFromProperties(properties),
      renewalNumber != null
        ? translateContext('renewal_number', { number: renewalNumber })
        : null,
    ];
  },
  payment_failed: (properties) => {
    const errorCode = readNonEmptyString(properties.error_code);

    return [
      (errorCode ? translatePaymentErrorCode(errorCode) : null) ??
        getTranslation('student_page.timeline.payment_errors.other'),
      formatPlanLabel(properties),
    ];
  },
  subscription_cancelled: (properties) => {
    const cancellationType = readNonEmptyString(properties.cancellation_type);
    const daysActive = readFiniteNumber(properties.days_active);

    return [
      cancellationType
        ? translateSubscriptionCancellationType(cancellationType)
        : null,
      formatPlanLabel(properties),
      daysActive != null && daysActive >= 0
        ? translateContext('subscription_days', {
            count: Math.round(daysActive),
          })
        : null,
    ];
  },

  app_opened: (properties) => {
    const referrerHost = readNonEmptyString(properties.referrer_host);

    return [
      referrerHost && !/(^|\.)befront\.(io|ru)$/i.test(referrerHost)
        ? translateContext('came_from', { host: referrerHost })
        : null,
    ];
  },
  plugin_opened: (properties) => [
    properties.is_first_open === true ? translateContext('first_open') : null,
  ],
  promo_code_applied: (properties) => [
    properties.is_valid === true
      ? translateContext('promo_code_valid')
      : properties.is_valid === false
      ? translateContext('promo_code_invalid')
      : null,
  ],
  reference_filter_selected: (properties) => {
    const name = readNonEmptyString(properties.filter_label);
    const cleared = properties.is_selected === false;

    if (!name) {
      return [cleared ? translateContext('filter_cleared_generic') : null];
    }

    return [
      translateContext(cleared ? 'filter_cleared' : 'filter_selected', {
        name,
      }),
    ];
  },
  student_profile_viewed: (properties) => [
    readNonEmptyString(properties.student_name),
  ],
  pricing_school_plan_clicked: (properties) => [
    readNonEmptyString(properties.school_name),
  ],
  profile_certificate_opened: (properties) => {
    const courseName = readNonEmptyString(properties.certificate_course_name);

    return [
      courseName
        ? translateContext('certificate_course', { name: courseName })
        : null,
    ];
  },
  profile_saved: (properties) => [
    properties.avatar_changed === true
      ? translateContext('avatar_changed')
      : null,
  ],
  lesson_report_submitted: (properties) => {
    const reasonsCount = readPositiveNumber(properties.reasons_count);

    return [
      reasonsCount != null
        ? translateContext('report_reasons', { count: reasonsCount })
        : null,
    ];
  },
};

STANDALONE_CONTEXT_BUILDERS.oauth_failed =
  STANDALONE_CONTEXT_BUILDERS.otp_failed;
STANDALONE_CONTEXT_BUILDERS.login_screen_viewed =
  STANDALONE_CONTEXT_BUILDERS.auth_viewed;
STANDALONE_CONTEXT_BUILDERS.paywall_viewed = buildPaywallContext;

/** Event-specific parts shown before the (non-redundant) course / lesson names. */
const CATALOG_CONTEXT_BUILDERS: Record<string, ContextBuilder> = {
  task_answered: buildTaskAnsweredContext,
  lesson_task_answered: buildTaskAnsweredContext,
  task_skipped: (properties) => {
    const seconds = readFiniteNumber(properties.time_spent_before_skip_sec);
    const duration =
      seconds != null && seconds > 0 ? formatWholeDuration(seconds) : null;

    return [duration ? translateContext('skipped_after', { duration }) : null];
  },
  lesson_navigated: (properties) => {
    const direction = readNonEmptyString(properties.direction);
    const targetName = readNonEmptyString(properties.target_lesson_name);
    const lessonIndex = readPositiveNumber(properties.lesson_index);
    const lessonsTotal = readPositiveNumber(properties.lessons_total);

    return [
      direction === 'jump' && targetName
        ? translateContext('navigation.jump_named', { name: targetName })
        : direction
        ? translateOptional(`${CONTEXT_PREFIX}.navigation.${direction}`)
        : null,
      /* `lesson_index` is the lesson the student left: «с урока 1 из 6». */
      lessonIndex != null && lessonsTotal != null
        ? translateContext('lesson_position_from', {
            number: lessonIndex,
            total: lessonsTotal,
          })
        : null,
    ];
  },
  lesson_exited: (properties) => [
    properties.is_completed === true
      ? translateContext('lesson_already_completed')
      : formatPercent(
          'lesson_progress',
          readFiniteNumber(properties.progress_percent),
        ),
  ],
  lesson_abandoned: (properties) => {
    const duration = formatWholeDuration(
      readFiniteNumber(properties.time_spent_sec),
    );

    return [
      formatSolvedTasks(readFiniteNumber(properties.tasks_done_before_abandon)),
      duration ? translateContext('time_in_lesson', { duration }) : null,
    ];
  },
  lesson_completed: (properties) => {
    const skipped = readPositiveNumber(properties.tasks_skipped);
    const duration = formatWholeDuration(
      readFiniteNumber(properties.total_time_sec),
    );

    return [
      formatSolvedTasks(readPositiveNumber(properties.tasks_done)),
      skipped != null
        ? translateContext('tasks_skipped', { count: skipped })
        : null,
      duration ? translateContext('in_duration', { duration }) : null,
    ];
  },
  learn_continue_clicked: (properties) => [
    formatPercent(
      'course_progress',
      readFiniteNumber(properties.progress_percent),
    ),
  ],
  trainer_mode_switched: (properties) => {
    const mode = readNonEmptyString(properties.mode);
    const modeLabel = mode ? translateTrainerMode(mode) : null;

    return [
      modeLabel ? translateContext('trainer_mode', { mode: modeLabel }) : null,
    ];
  },
  paywall_opened: buildPaywallContext,
  paywall_dismissed: buildPaywallContext,
  paywall_checkout_clicked: buildPaywallContext,
  bonus_content_purchase_clicked: (properties) => [
    formatPlacement(properties),
    formatPriceFromProperties(properties),
  ],
  figma_account_connect_clicked: (properties) => [formatPlacement(properties)],
};

/** Events whose subtitle names the lesson even when the catalog could not resolve it. */
const LESSON_FALLBACK_EVENT_KEYS = new Set([
  'lesson_viewed',
  'lesson_started',
  'lesson_completed',
  'lesson_abandoned',
  'lesson_rated',
  'lesson_exited',
  'task_started',
  'task_completed',
  'task_skipped',
  'task_answered',
  'lesson_task_answered',
]);

export type FormatEventContextOptions = {
  omitLessonName?: boolean;
  omitModuleName?: boolean;
  /** The row repeats its screen's lesson/module: no course / lesson names at all. */
  omitCatalog?: boolean;
  studentEmail?: string | null;
};

/** Course / lesson names that the catalog block does not already show. Never ids. */
const buildCatalogParts = (
  translationKey: string,
  properties: Record<string, unknown>,
  options?: FormatEventContextOptions,
): string[] => {
  if (options?.omitCatalog) {
    return [];
  }

  const parts: string[] = [];
  const moduleName = readNonEmptyString(properties.module_name);
  const lessonName = readNonEmptyString(properties.lesson_name);

  if (!options?.omitModuleName && moduleName) {
    parts.push(moduleName);
  }

  if (!options?.omitLessonName) {
    if (lessonName) {
      parts.push(lessonName);
    } else if (
      LESSON_FALLBACK_EVENT_KEYS.has(translationKey) &&
      readNonEmptyString(properties.lesson_id)
    ) {
      parts.push(formatMissingLessonLabel(properties));
    }
  }

  return parts;
};

/** Builds a compact, human subtitle from analytics event properties (no ids / raw values). */
export const formatEventContext = (
  eventName: string,
  properties?: Record<string, unknown>,
  options?: FormatEventContextOptions,
): string | null => {
  if (!properties) {
    return null;
  }

  const translationKey = eventNameToTranslationKey(eventName);
  const standaloneBuilder = STANDALONE_CONTEXT_BUILDERS[translationKey];

  if (standaloneBuilder) {
    return capitalizeFirst(joinParts(standaloneBuilder(properties, options)));
  }

  const specificParts =
    CATALOG_CONTEXT_BUILDERS[translationKey]?.(properties, options) ?? [];

  return capitalizeFirst(
    joinParts([
      ...specificParts,
      ...buildCatalogParts(translationKey, properties, options),
    ]),
  );
};
